import { Dispatch, FC, useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { Action } from 'redux';

import { CurrencySymbolEnum } from '../../lib/enums/currency.enum';
import { MarketCapCategory, MarketCapData, MarketCapsParamType } from '../../lib/models/fundamental/types';
import { RootState } from '../../lib/store';
import { marketCapRadarCategory, setMarketCapRadarCategory } from '../../lib/store/discover/marketCapRadarState';
import { getMarketCaps as getMarketCapsCall } from '../../lib/store/fundamental';
import { FundamentalCall } from '../../lib/store/fundamental/types';
import { defaultLogoBase64 } from '../../lib/util/assets/logos';
import { isCallStatusPending, isCallStatusReady } from '../../lib/util/error-handling/StatusByCallHelpers';

import { usePriceAndChartLoading, useSubPageTitle } from '../../hooks';
import { useSubscriptionManager } from '../../hooks/useSubscriptionManager';
import {
  MARKET_CAP_RADAR_DEFAULT_SORT_DIRECTION,
  MarketCapSortDirection,
  MarketCapSortField,
} from '../../shared_components/MarketCapRadar/MarketCapRadar.constants';
import MarketCapRadarInternal from '../../shared_components/MarketCapRadar/MarketCapRadarInternal/MarketCapRadarInternal';
import {
  MarketCapRadarInternalRowModel,
} from '../../shared_components/MarketCapRadar/MarketCapRadarInternal/MarketCapRadarInternal.types';
import { UI_ROUTES } from '../../util/routes';

import { hasMoreMarketCapPages, sortMarketCapRows } from './marketCapRadar.helpers';

const GAINERS_TAB_INDEX = 0;
const LOSERS_TAB_INDEX = 1;

const MarketCapRadarInternalWrapper: FC<ReduxProps> = (props: ReduxProps): JSX.Element => {
  const {
    getMarketCaps,
    isMarketCapsReady,
    isMarketCapsPending,
    marketCapsData,
    companyLogoAndName,
  } = props;
  const { gainers, losers } = marketCapsData.responseData;

  const location = useLocation();
  const titleRef = useSubPageTitle<HTMLDivElement>();
  const navigate = useNavigate();
  const { state } = location;

  const selectedCategory = marketCapRadarCategory.use();

  const [activeTabIndex, setActiveTabIndex] = useState<number>(
    () => (state?.isGainersSelected === false ? LOSERS_TAB_INDEX : GAINERS_TAB_INDEX),
  );
  const [sortField, setSortField] = useState<MarketCapSortField | null>(null);
  const [sortDirection, setSortDirection] = useState<MarketCapSortDirection>(
    MARKET_CAP_RADAR_DEFAULT_SORT_DIRECTION,
  );

  const activeTabPage = activeTabIndex === GAINERS_TAB_INDEX ? gainers : losers;
  const activeTabStocks = activeTabPage.content;

  const sortedStocks = useMemo(
    () => sortMarketCapRows(activeTabStocks, sortField, sortDirection),
    [activeTabStocks, sortField, sortDirection],
  );

  const rows = useMemo((): MarketCapRadarInternalRowModel[] => sortedStocks.map((item: MarketCapData) => {
    const entry = companyLogoAndName[item.symbol];

    return {
      ...item,
      // Single market: the price cache is keyed by the plain symbol and prices are in dollars.
      countryDesignation: '',
      lookupSymbol: item.symbol,
      currencySymbol: CurrencySymbolEnum.USD,
      logo: entry?.logo ?? defaultLogoBase64,
      logoUrl: item.url ?? entry?.url ?? '',
      displayName: item.name ?? entry?.name ?? '',
    };
  }), [sortedStocks, companyLogoAndName]);

  const isLoading = usePriceAndChartLoading({
    stocks: activeTabStocks,
    isApiPending: isMarketCapsPending,
  });

  const symbolsToSubscribe = useMemo(() => activeTabStocks.map(item => ({
    symbol: item.symbol,
    stockExchangeCode: item.stockExchangeCode,
    countryDesignation: companyLogoAndName[item.symbol]?.countryDesignation,
  })), [activeTabStocks, companyLogoAndName]);

  useSubscriptionManager({
    symbols: symbolsToSubscribe,
    enabled: isMarketCapsReady && symbolsToSubscribe.length > 0,
  });

  useEffect(() => {
    getMarketCaps({ category: selectedCategory });
  }, [getMarketCaps]);

  const handleCategoryChange = (category: MarketCapCategory) => {
    setMarketCapRadarCategory(category);
    getMarketCaps({ category });
  };

  const handleSortClick = (field: MarketCapSortField) => {
    if (field === sortField) {
      setSortDirection(prev => (prev === 'desc' ? 'asc' : 'desc'));
      return;
    }

    setSortField(field);
    setSortDirection(MARKET_CAP_RADAR_DEFAULT_SORT_DIRECTION);
  };

  const handleTabChange = (index: number) => {
    setActiveTabIndex(index);
    // Update browser history so the tab is preserved when navigating back
    navigate(location.pathname, {
      replace: true,
      state: { isGainersSelected: index === GAINERS_TAB_INDEX },
    });
  };

  const handleSymbolClick = (symbol: string) => {
    navigate(`${UI_ROUTES.symbol}/${symbol}`);
  };

  const handleLoadMore = () => {
    getMarketCaps({ category: selectedCategory, page: activeTabPage.number + 1 });
  };

  const handleGoBack = () => navigate(UI_ROUTES.discover);

  return (
    <MarketCapRadarInternal
      rows={rows}
      activeTabIndex={activeTabIndex}
      selectedCategory={selectedCategory}
      sortField={sortField}
      sortDirection={sortDirection}
      isPending={isMarketCapsPending}
      isLoading={isLoading}
      isNoData={!isMarketCapsPending && activeTabStocks.length === 0}
      hasMorePages={hasMoreMarketCapPages(activeTabPage)}
      onTabChange={handleTabChange}
      onCategoryChange={handleCategoryChange}
      onSortClick={handleSortClick}
      onLoadMore={handleLoadMore}
      onSymbolClick={handleSymbolClick}
      isAce
      titleRef={titleRef}
      onGoBack={handleGoBack}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  isMarketCapsReady: isCallStatusReady(FundamentalCall.getMarketCaps, state.fundamental.statusByCall),
  isMarketCapsPending: isCallStatusPending(FundamentalCall.getMarketCaps, state.fundamental.statusByCall),
  marketCapsData: state.fundamental.marketCapsData,
  companyLogoAndName: state.fundamental.companyLogoAndName,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  getMarketCaps: (payload: MarketCapsParamType) => dispatch(getMarketCapsCall(payload)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(MarketCapRadarInternalWrapper);
